import React from 'react';

import { ContentWrapper, ContentArea } from './styles.ContentContainer';
import { NavbarSide } from '../Navbar/NavbarSide';
import {
  CardSection1,
  CardSection2,
  CardSection3,
  CardSection4,
  CardSection5,
} from '../CardSection/CardSection';
import { LatestComments } from '../LatestComments/LatestComments';
import { TestComments } from '../TestComments/TestComments';
import { Waypoint } from '../Waypoint/Waypoint';

import ImageFellows from '../../assets/images/jai-fellows.jpg';
import ImageRobot from '../../assets/images/robot.jpg';
import ImageProdigy from '../../assets/images/prodigy.jpg';
import ImageArchitecture from '../../assets/images/achitecture.jpg';
import ImageGrafana from '../../assets/images/grafana.jpg';
import ImageTeamsPromt from '../../assets/images/teams-prompt.jpg';
import ImageTeamsSuccess from '../../assets/images/teams-success.jpg';
import ImageTableA from '../../assets/images/table-a.jpg';
import ImageTableB from '../../assets/images/table-b.jpg';
import ImageLearnings from '../../assets/images/learnings.jpg';
import ImagePowerAutomate from '../../assets/images/power-automate.jpg';
import ImageVideoInterview from '../../assets/images/userresearch-still.jpg';
import VideoInterview from '../../assets/videos/UT-User_Research_low.mp4';

type Props = {
  currentSection: string;
  setCurrentSection: any;
};

export const ContentContainer: React.FC<Props> = ({
  currentSection,
  setCurrentSection,
}) => {
  return (
    <ContentWrapper>
      <NavbarSide currentSection={currentSection} />

      <ContentArea>
        <Waypoint
          id="introduction"
          action={setCurrentSection}
          initialInView={true}
        >
          <h1 id="introduction">What’s there, what’s missing &#128054;</h1>

          <p className="smallMarginBottom">
            We’re using AI to identify user comments that address the BR or MDR
            newsrooms to foster a constructive dialogue with our audiences. A
            joint project of Bayerischer Rundfunk (BR), Mitteldeutscher Rundfunk
            (MDR) and ida as part of the LSE JournalismAI Fellowship Programme.
            <br />
          </p>

          <p className="bigMarginBottom greyText">
            Authors: Jaime Avalos Mongil (ida), Philipp Gawlik (BR), Martin Paul
            (MDR), Jörg Pfeiffer (BR); Published: December 2022
          </p>

          <p className="noMarginBottom">
            These are the latest comments we recognised addressing the newsrooms
            (in beta):
          </p>

          <LatestComments />

          <h4 className="bigMarginTop">Try our Demo</h4>

          <p className="smallMarginBottom">
            Write a comment addressing the newsroom. Please keep in mind that
            our model is trained on real world comments and only German comment
            with a certain length are supported. Here are some valid examples:
          </p>
          <ul>
            <li>“Vielen Dank für die gute Arbeit @mdr”</li>
            <li>
              “Um Himmelswillen: zieht doch endlich diesen Artikel zurück...”
            </li>
            <li>“Liebe Redaktion, ich habe einen Fehler gefunden!”</li>
          </ul>
          <p className="smallMarginBottom">
            A test comment like the following might be ignored:
          </p>
          <ul>
            <li>
              <del>“Liebe Redaktion!”</del>
            </li>
          </ul>

          <TestComments />

          <h3>Introduction</h3>

          <p>
            <strong>
              “Hallo Redaktion, in dem Artikel ist ein Rechtschreibfehler!”
              (“Hello editorial, there’s a typo in the article!”)
            </strong>
          </p>

          <p>
            An important part of journalistic work is the exchange and
            conversation with users. The German public channels{' '}
            <a href="https://www.br.de/nachrichten/">BR</a> and{' '}
            <a href="https://www.mdr.de/nachrichten/index.html">MDR</a> each
            receive more than 10,000 comments a day: statements, opinions about
            the published content, but also questions about the topics and
            research, tips for further contributions or about errors, praise and
            criticism directly to the authors and newsroom teams. The editorial
            teams should respond quickly and enter into an exchange with the
            readers. What looks simple at first glance is not trivial in
            editorial practice. However, algorithms in general and machine
            learning in particular can help.
          </p>

          <p>
            How do you recognize the comments and posts where editorial staff
            and authors are directly addressed as quickly as possible? In the
            "🐶 What’s there, what’s missing” project, we took six months to
            identify the various addresses to the newsrooms and develop
            techniques to recognize them automatically.
          </p>

          <p>
            After an initial user research in the editorial offices of BR and
            MDR, we classified thousands of comments and trained an AI model.
            This way we built a prototype that will support the editorial
            offices in detecting comments that address the newsrooms and send a
            message to the Community Management.
          </p>
        </Waypoint>

        <Waypoint id="journalism-ai" action={setCurrentSection}>
          <h2 id="journalism-ai">Journalism AI Fellowship of LSE</h2>

          <figure>
            <img src={ImageFellows} alt="JournalismAI Fellows" loading="lazy" />
            <figcaption>
              Participants of the Journalism AI Fellowship 2022 (Image rights:
              JournalismAI/LSE)
            </figcaption>
          </figure>

          <p>
            The{' '}
            <a href="https://www.lse.ac.uk/media-and-communications/polis/JournalismAI/Fellowship-Programme">
              JournalismAI Fellowship
            </a>{' '}
            of the{' '}
            <a href="https://www.lse.ac.uk/">
              London School of Economics and Political Science’s
            </a>{' '}
            aims to empower media organizations and journalists to use
            artificial intelligence responsibly in their day-to-day work. The
            Fellowship is part of the LSE’s Media Think Tank 'Polis' and
            financially supported by the Google News Initiative.
          </p>

          <p>
            Teams of journalists, developers, and machine learning experts from
            media companies and journalism initiatives around the world have
            been working together in the Fellowship since June 2022 to develop
            and critically discuss the use of algorithms and automation in
            newsrooms across a range of projects.
          </p>

          <p>
            In order to further develop specific solution approaches in a
            concentrated manner, two media houses with similar project ideas
            have joined forces during the Fellowship’s work phase to collaborate
            on a joint project. With similar initial ideas and technical
            prerequisites, the two public broadcasters{' '}
            <a href="https://www.br.de/nachrichten/">BR</a> and{' '}
            <a href="https://www.mdr.de/nachrichten/index.html">MDR</a> with the{' '}
            <a href="https://ida.me">digital agency ida</a> teamed up.
          </p>

          <p>
            Both media houses had previously gained experience in the field of
            automation in journalism, BR with its own AI and Automation Lab and
            MDR together with ida, among other things, in the implementation and
            development of a model for automated election reporting.
          </p>
        </Waypoint>

        <Waypoint id="initial-ideas" action={setCurrentSection}>
          <h2 id="initial-ideas">Initial ideas</h2>

          <p>
            BR and MDR, together with ida, have the mandate as public
            broadcasters to enable their users to participate in democratic
            discourse by, among other things, presenting a broad spectrum of
            topics, perspectives and opinions in their contributions and
            articles and to enter into conversation with them.
          </p>

          <p>
            In the associated comments, users can enter into an exchange with
            each other and with the editorial offices. For this reason, at the
            beginning of the fellowship, the common question was how to help
            editorial offices improve and expand the dialogue with users with
            the help of algorithms and AI techniques.
          </p>

          <p>
            For newsrooms, the large number of daily comments and posts makes it
            very challenging to effectively sift through user feedback and use
            it for editorial work and further discussion. Algorithms and machine
            learning models, especially in the area of topic modelling and
            natural language processing (NLP), can support newsroom teams in
            this process.
          </p>

          <p>
            At the beginning of the project, both teams wanted to find out how
            exactly and under what circumstances and developed a joint project
            idea under the title: "🐶 What’s there, what’s missing”.
          </p>

          <p>Several directions of development were possible:</p>

          <ul>
            <li>
              <strong>Identifying topics in user comments</strong>
              <br />
              What are the topics that readers are most concerned about? Our
              initial idea, which also gave the project its name, was to find
              out which topics our readers pick up on most in their comments. We
              wanted to find out whether we have gaps in our reporting and
              whether there are topics we should report on more extensively than
              before.
            </li>
            <li>
              <strong>Faster fact-checking</strong>
              <br />
              Comments often present a different view of the topic of the
              article. In some cases, theses are put forward that the community
              management and the respective author cannot simply confirm or
              refute. One initial idea was to use AI to automatically collect
              suitable data on the topic of the comment and pass it on to the
              fact-checking team.
            </li>
            <li>
              <strong>Detecting comments addressing the newsroom</strong>
              <br />
              The aim of community management is to maintain and strengthen a
              respectful dialogue with its users. To achieve this, however, it
              is important to pay particular attention to those readers who
              address the editorial team with constructive questions and
              criticism. The AI should help here to recognise comments that are
              directed at the editorial team and where readers want to enter
              into a dialogue.
            </li>
          </ul>
          <p>
            To find out what helps the editorial team the most, we conducted an
            extensive user survey. Initially by the User Research department of
            ida.
          </p>
        </Waypoint>

        <Waypoint id="user-research" action={setCurrentSection}>
          <h2 id="user-research">User Research</h2>

          <figure>
            <video preload="metadata" controls poster={ImageVideoInterview}>
              <source src={VideoInterview} type="video/mp4" />
              <p>This browser does not support HTML5 video.</p>
            </video>
            <figcaption>Interview in German</figcaption>
          </figure>

          <p>
            For the 6-month JournalismAI Fellowship, it was important to the
            team from BR, MDR and ida from the very beginning not to develop
            without taking into account the needs of the editorial offices.
            Therefore, after the first brainstorming phase, we initiated a user
            research in which we asked the needs of different community managers
            from BR and MDR.
          </p>

          <p>
            The user survey was initially supported by the ida User Research
            department and then conducted by us alone. It is neither
            representative nor can it be compared to a professional user survey,
            but it nevertheless helped us to gain valuable insight into the work
            and needs of community management.
          </p>

          <CardSection5 />

          <p>
            In a total of seven sessions, we used a standardised questionnaire
            to explore the work processes, wishes and ideas of our colleagues
            and came across different requirements and workflows at BR and MDR.
            One thing was the same in all teams:
          </p>
          <br />
          <strong>
            The valuable dialogue with the readers needs time and especially
            questions from the users to the editors have to be recognised and
            forwarded as quickly as possible.
          </strong>
        </Waypoint>

        <Waypoint id="final-concept" action={setCurrentSection}>
          <h2 id="final-concept">
            Final Product: Recognise comments addressing the Newsroom
          </h2>

          <p>
            After the user survey, it became clear: the colleagues from
            community management would like automated support in quickly
            recognising reader comments in which the editorial team is directly
            addressed. The AI’s task would be to recognise the different types
            of contact and forward them to the responsible staff.
          </p>

          <p>
            Because with thousands of comments per day, it is a challenge both
            for the community managers and for classic search algorithms to
            recognise all those that are addressed to the editorial team. The
            community managers know from their daily work that contact requests
            do not always contain a direct form of address such as “@BR24” or
            “@MDR” or “to the editor”. This is where machine learning is
            supposed to help.
          </p>

          <p>
            In its basic features, the system should analyse the user comment
            directly after it has been created and, if the result is positive,
            contact the community management and the respective author via the
            desired communication channels.
          </p>

          <p>
            For example, if a reader writes “There’s a typo here”, a message
            should be sent to all staff members who are currently sitting at the
            newsdesk and can correct errors.{' '}
            <strong>
              The quick response helps to ensure the quality of the page and
              readers notice that their constructive criticism is directly
              implemented.
            </strong>{' '}
            Through this positive reinforcement, the editors want to establish a
            sustainable and constructive dialogue with their readers.
          </p>

          <p>
            The quick recognition of hints to the editors in the user comments
            is also a great help in fact-checking. For example, if the comment
            “The data source of the article is incomplete” comes in, the
            respective author should be contacted directly, as only he or she
            has the necessary expertise to check this statement and start any
            fact-checking. These processes take time and the sooner the
            editorial team becomes aware of information from readers, the higher
            the chance of being able to give a fact-based answer.
          </p>

          <p>
            The user survey also showed that comments with positive feedback
            such as “Thank you to the editorial team” as well as constructive
            criticism should be gladly forwarded to all authors involved.
          </p>

          <figure>
            <img
              src={ImageRobot}
              alt="A robot holding a comment in his right and MS Teams in his left hand"
              loading="lazy"
            />
            <figcaption>
              Our comment detection workflow: From comments under an article to
              notifying the editorial team in MS Teams.
            </figcaption>
          </figure>

          <h4>Getting into the workflow</h4>

          <p>
            To make our system as simple as possible for editors, we decided
            against a stand-alone programme and in favour of integration into
            already existing systems and work routine
          </p>

          <p>
            The first step was to analyse and compare similarities and
            differences between the BR and MDR editorial teams. Differences were
            to be found above all in the distribution of tasks to the editors,
            such as moderation according to the respective{' '}
            <a href="https://www.br.de/service/kommentare-netiquette-richtlinien-110.html">
              BR
            </a>{' '}
            or{' '}
            <a href="https://www.mdr.de/service/kommentarrichtlinien100.html">
              MDR
            </a>{' '}
            etiquettes and the passing on of information.
          </p>

          <p>
            In the programmes and systems used, rather similarities could be
            found. Both editorial teams use similar comment management systems
            that are integrated into their editorial content management systems
            (CMS). Communication with the team and the passing on of information
            is done via e-mail or Microsoft Teams for both.
          </p>

          <p>
            Due to these similarities, we can easily offer a proposal for a
            commentary workflow that can be integrated into the work processes
            of the editors of both broadcasting houses with as little effort as
            possible in a later productive use and, in the long term, integrated
            as a plug-in into existing moderation tools.
          </p>

          <p>
            The workflow is set up in such a way that the incoming comments are
            examined by the NLP model and pattern recognition. If a mention of a
            newsroom is found, the comment is forwarded to a MS Teams chat or
            channel. Editors who have subscribed to the specific channel are
            informed about the incoming comment.{' '}
          </p>

          <p>
            In the following, several options can be selected: The comment can
            be moderated and answered in the comment management programme, the
            information can be passed on to the responsible editors and authors
            or the option can be chosen that there is no addressing in the
            comment. This allows the NLP model to be continuously trained and
            refined.
          </p>
        </Waypoint>

        <Waypoint id="technical-description" action={setCurrentSection}>
          <h2 id="technical-description">Technical Description</h2>
          <p>
            This section describes the technical infrastructure of the project.
            We included our learnings as much as possible so they can be useful
            to others when attempting to build such a tool.
          </p>
          <h3>Data Sources</h3>
          <p>
            The data used to train our models consists of user comments on
            website articles and social media postings from Bayerischer Rundfunk
            and Mitteldeutscher Rundfunk. Even though the focus in this project
            is on article comments, we additionally used social media comments
            to get a database as large as possible containing different variants
            of author and newsroom addressing.
          </p>
          <p>A list of the specific pages and accounts can be found here:</p>
          <CardSection2 />
          <p>
            The comments were both scraped from the BR and MDR websites and
            later made available in real time through access to an API. The
            comments used are from 2022.
          </p>
          <h4>Labeling with Prodi.gy</h4>
          <figure>
            <img src={ImageProdigy} alt="Prodigy tool" loading="lazy" />
            <figcaption>
              Prodigy is a tool that can be used to mark passages of text in
              order to train a machine learning model. (Source: Screenshot
              prodigy/MDR)
            </figcaption>
          </figure>
          <p>
            In order to train our own model for recognising mentions of the
            editorial team in user comments, we needed a data set in which there
            are enough comments with examples of mentions. However, since this
            requires sifting through and annotating thousands of comments as
            efficiently as possible, we need a tool that facilitates this
            process.
          </p>
          <p>
            We chose the annotation tool <a href="https://prodi.gy/">Prodigy</a>{' '}
            which allows you to quickly sift through the comments from the
            datasets and mark the relevant passages when you find a mention.
          </p>
          <p>
            In this way, we labelled around 17,500 comments within four working
            days and found around 2% examples with newsroom addresses – i.e.
            positive comments in the technical sense.
          </p>
          <p>
            As an annotation schema we deliberately chose a named entity
            recognition style annotation. The schema allowed us to learn about
            common patterns of mentions of the moderation team/authors in the
            comments. In addition, we were able to derive binary text labels as
            following:
          </p>
          <ul>
            <li>label 0: the comment doesn’t include annotated mentions</li>
            <li>label 1: the comment includes annotated mentions</li>
          </ul>
          <p>We were able to draw four main conclusions from this:</p>
          <ul>
            <li>
              Only an small proportion of all comments also addresses the
              editorial team. To accquire enough comments for a first round of
              training during the time of the fellowship we had to speed up the
              annotation process (see chapter Data Augmentation).
            </li>
            <li>
              Many addresses contain the names of the respective editors,
              authors or keywords such as “editor”, “article”, “author”. In a
              first attempt, a Named Entity Recognition (NER) model was trained
              with the annotated data. Later, however, we trained a Text
              Classification Model, since this approach, in contrast to the NER
              method, looks at the comment as a whole and thus analyses, among
              other things, the linguistic style or the topic. This method is
              therefore much more suitable for ambiguous or indirect addresses
              of the newsroom.
            </li>
            <li>
              Constant re-training of the models is necessary, as the character
              of the comments, the addresses and the topics can change greatly
              over a short period of time.
            </li>
            <li>
              In order to be able to train the NLP model accurately, one needs
              clear definitions of what is and what is not a relevant addressing
              of the newsroom. Even if editors or authors are mentioned, the
              comment is not necessarily a question, a hint or a call to action.
              We have defined rules and guidelines when a comment should be
              recognised as a relevant addressing of the newsroom:
            </li>
          </ul>
          <CardSection1 />
          <h4>Data Augmentation</h4>
          <p>
            After our first round of annotations we acquired 17,439 comments of
            social media and website comments addressed to MDR and BR. Within
            these comments around 2% of comments addressed the moderation team
            of either MDR or BR.
          </p>
          <p>
            On the one hand this share is a good fit for our solution approach,
            because it justifies the necessity of support in finding these rare
            comments and makes MS Teams a suitable interface for the
            publication. While on the other hand the small amount of training
            example is far to less for a machine learning approach. To handle
            this issue we decided to augment our training corpus with generated
            comments.
          </p>
          <p>
            To generate the artificial comments we chose OpenAiI’s language
            model GPT-3 and applied its comment generation API. We randomly
            paired 500 article teasers from the BR website with a roster of
            context prompts. With these pairs we generated 1449 comment
            candidates. Then we went on to annotate these candidates with
            prodigy following the schema we also applied to the rest of the
            comments. This resulted in 1421 (98%) usable positive labelled
            comments that we added to our corpus.
          </p>
          <p>
            Please note, that the generated comments are merely meant to kick
            off the training of the prototype model. Retraining of the model in
            later iterations of our system will ignore the generated comments
            and solely depend on authentic comments.
          </p>
          <p>
            This augmentation step involving the service of OpenAI prevents us
            from open sourcing our corpus because of legal restrictions from
            OpenAI. The list of context prompts can be found in the Appendix 1.
            Note the article teaser in respect is substituted into the context
            prompt.
          </p>
          <CardSection3 />
          <h4>Solution Architecture</h4>
          <figure>
            <img
              src={ImageArchitecture}
              alt="the projects Cloud infrastructure"
              loading="lazy"
            />
            <figcaption>The projects Cloud infrastructure</figcaption>
          </figure>
          <p>
            Our architecture is hosted on Google Cloud and consists of several
            ownstanding microservices that communicate to each other via API. We
            store a raw version of the data on a Google Cloud Bucket and a
            usable version of the data on a Postgres database. A machine
            learning model detects whether new comments request an interaction
            from the moderating team. If so, the comments are published on a
            Microsoft Teams channel.
          </p>
          The project repository can be found here:{' '}
          <a href="https://github.com/br-data/wtwm-topic-modelling">Github</a>
          <p></p>
          <h3>Data Loading</h3>
          <p>
            We built two APIs for data loading, one for each of the
            organizations. In the end JournalismAI kindly provided funding to
            build an API with which we could programmatically have access to the
            comments. The APIs we built make use of this API to pull comments
            almost on real time.
          </p>
          <h4>Data Storage</h4>
          <p>
            The API first stores a raw version of the data into a Google Cloud
            Bucket and then sends the comments to a Postgres Database. At first
            we tried to use BigQuery as a database solution but we discovered
            that BigQuery has a hard limit of 1,500 table modifications per day.
            This limit was not enough to perform almost real time reporting.
            Therefore, we switched to a Postgres database.
          </p>
          <figure>
            <img
              src={ImageGrafana}
              alt="Postgres database with comments"
              loading="lazy"
            />
            <figcaption>Postgres database with comments</figcaption>
          </figure>
          <h4>Model Workflow</h4>
          <p>
            We store the model on a Google Cloud Bucket and load it using a
            /reload_model into the /find mentions API. The latter pulls data
            from the Postgres database, infers whether there are any comments
            containing a request for interaction and sends this information back
            to the Database.
            <br />
            We also would like an API that automatically re-trains the model
            when there are enough new comments.
          </p>
          <h4>Comment Publishing</h4>
          <p>
            In order to publish the comments we built three various APIs. On our
            first attempt we used{' '}
            <a href="https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook">
              MS teams webhooks
            </a>
            . However, some of our organizations did not allow this for security
            reasons. Then we switched to a{' '}
            <a href="https://powerautomate.microsoft.com">Power Automate</a>{' '}
            solution. Power Automate is a microsoft product that allows the
            creation of “workflows” to perform actions on MS teams channels and
            chats. It also allows the creation of APIs to perform these actions.
          </p>
          <p>
            When the system finds a comment with a direct mention, a publisher
            API sends the comment to Microsoft teams. Below you can see the
            Power Automate flow for the publishing of our comments:
          </p>
          <figure>
            <img
              src={ImagePowerAutomate}
              alt="Power Automate workflow"
              loading="lazy"
              style={{ width: '65%' }}
            />
            <figcaption>Power Automate workflow</figcaption>
          </figure>
          <p>
            It basically creates an API which needs the comment text and id, the
            text is piped into an "
            <a href="https://learn.microsoft.com/en-us/power-automate/overview-adaptive-cards">
              adaptative card
            </a>
            ". Adaptive cards can be created using{' '}
            <a href="https://adaptivecards.io/designer/">this tool</a>. The
            cards that we are publishing look like the following:
          </p>
          <figure>
            <img
              src={ImageTeamsPromt}
              alt="Message in MS Teams"
              loading="lazy"
              style={{ width: '65%' }}
            />
            <figcaption>Message in MS Teams</figcaption>
          </figure>
          <p>
            It gives a comment text and asks the user to provide feedback. The
            feedback the user can provide is whether the comment actually
            contains a request for interaction or not. When the user hits the
            button “submit” this useful feedback submits a request to the API
            /send_feedback and updates the comments on the database. This
            information can be later used to retrain the model.
          </p>
          <figure>
            <img
              src={ImageTeamsSuccess}
              alt="Message approved in MS Teams"
              loading="lazy"
              style={{ width: '85%' }}
            />
            <figcaption>Message approved in MS Teams</figcaption>
          </figure>
          <p>
            Once the user has submitted his answer he is thanked for the action
            and given a link to moderate the comment.
          </p>
          <h3>Deployment</h3>
          <p>For the development of the Application we used Google Workflow.</p>
          <h3>Model Training</h3>
          <p>
            As described in chapter data source, we deliberately choose a named
            entity recognition style annotation. The schema allowed us to learn
            about common patterns of mentions of the moderation team/authors in
            the comments. In addition, we were able to derive binary text labels
            as following:
          </p>
          <ul>
            <li>label 0: the comment doesn’t include annotated mentions</li>
            <li>label 1: the comment includes annotated mentions</li>
          </ul>
          <p>
            Equipped with these one corpora with two annotation levels we
            approached our modelling task at hand as following:
          </p>
          <ul>
            <li>Baseline Approach</li>
            <li>Named Entity Recognition (NER)</li>
            <li>Text Classification</li>
            <li>Hybrid</li>
          </ul>
          <p>
            Since the time of the JournalismAI fellowship is very limited and we
            were not able to implement step 4. For the principle of completeness
            we add our conceptual work to this documentation to offer a
            foundation for future work as well.
          </p>
          <h4>Baseline</h4>
          <p>
            Our baseline is a regular expression (abbr. „Regex”) based approach
            that includes a collection of common patterns used to address the BR
            and MDR media houses in social media and website comments. These
            patterns were collected during the annotation process and can be
            found in Appendix 2.
          </p>
          <CardSection4 />
          <p>
            Our baseline approach is meant as a lower boundary metric to our
            model training approaches. While the approach captures frequent
            patterns used in referencing the BR and MDR as in:
          </p>
          <ul>
            <li>“@MDR redaktion ich liebe euch!”</li>
            <li>“Der Autor soll mal deutsch lernen!”</li>
          </ul>
          <p>
            it clearly lacks the ability to capture fuzzy mentions in comments
            regarding typography, fact checking or general references to the
            authors and moderation team, as in:
          </p>
          <ul>
            <li>“Ach, wie wunderbar zu lesen!”</li>
            <li>“Was hier verschwiegen wird, ist der Umstand ……"</li>
          </ul>
          <p>
            These shortcomings of the pattern based approach lead us to
            investigate machine learning based approaches.
          </p>
          <h4>Named Entity Recognition</h4>
          <p>
            Our annotation schema allowed us to experiment with the approach of
            Named Entity Recognition. As the name suggests, this type of model
            recognizes “entities” present in a text. Some classical examples of
            entities are organizations like MDR or BR, a person like Angela
            Merkel, Brad Pitt, Jamiè Avalos or places like Munich or Berlin.
          </p>
          <p>
            We ruled out training a new NER model from scratch with our corpus
            of ~ 19,000 samples. But we decided to invest some effort into the
            research question, if a present NER model for names can be adapted
            for our present task of finding references like @BR or @MDR. We took
            the base spaCy german model for the NERecognition task and fine
            tuned it to recognize direct mentions to MDR and BR.
          </p>
          <p>
            The evaluation results can be found in Figure 1. Based on these
            results, we ruled out this approach for our task at hand. As
            mentioned in the chapter BASELINE, references to the media
            house/moderation team can be very abstract, containing many tokens.
            NER models do not perform well on entities that contain many tokens.
            <br />
            The small size of our training data adds to the negative results as
            well and could be the reason for the suspiciously high amount of
            false positive predictions because it did not weaken the effect of
            entities that have been relevant to the model before fine tuning.{' '}
          </p>
          <h4>Text Classification</h4>
          <p>
            To approach the fuzzy worded mentions in comments (e.g. in chapter
            Baseline) we decided to apply a binary text classification approach
            to classify comments with and without mentions. Because our entity
            labeling approach allowed us to assign binary labels to our comment
            examples we were able to use our corpus right away. Our insights in
            the limited number of examples in our corpus from previous
            approaches did let us decide to finetune present foundation models
            that were provided on{' '}
            <a href="https://huggingface.co/">Hugging Face</a>. We choose this
            approach to incorporate linguistic knowledge on the German language
            that is not present in our corpus data.
          </p>
          <p>
            We fine tuned german GPT-2 to binary classify whether or not a
            comment includes mentions for the MDR or BR based on our training
            corpus. More information about GPT-2 can be found here:{' '}
            <a href="https://huggingface.co/dbmdz/german-gpt2">
              https://huggingface.co/dbmdz/german-gpt2
            </a>
          </p>
          <p>
            The following training configuration results in the best outcome.
            The configuration is set in part to work best with our
            infrastructure (further information in chapter Deployment) and in
            part to optimize the results.
          </p>
          <ul>
            <li>evaluation_strategy : epoch</li>
            <li>learning_rate: 2e-7</li>
            <li>best result after epoch: 3</li>
            <li>weight_decay: 0.1</li>
            <li>metric_for_best_model: precision</li>
          </ul>
          <p>
            The low number of train epochs is conspicuous. In our experiments,
            the precision grew for some epochs (depending on the settings) and
            started to decline continuously from there for the training as well
            as test data. The present model is the model with the best precision
            before the metric started to decline.
          </p>
          <p>
            A possible explanation could be the imbalance of positive and
            negative examples in our train corpus. The imbalance lets the
            training tend to ignore the small amount of positive samples to
            maximise the train result. A possible approach could be to adjust
            the loss function of the training pipeline. But we came to the
            conclusion that a bigger train corpus with more positive examples
            would be a better next step.
          </p>
          <p>
            Figure 2 gives our human evaluation of the classification results.
            The evaluation stems from working with our pipeline based on our
            classification model and turned out to be surprisingly good already.
            The model can be found here:{' '}
            <a href="https://huggingface.co/aiautomationlab/wtwm-gpt2-based-mentions-detector">
              Huggingface
            </a>
          </p>
          <h4>Hybrid</h4>
          <p>
            In addition to the approaches so far, we like to add knowledge about
            the media house in respect to the model to increase the performance
            results. Additional knowledge about the handles of the members of
            the moderation team and authors of the articles would be a obvious
            choice and could be applied in disjunctive manner:
          </p>
          <ul>
            <li>contains_mentions(comment) V contains_handle(comment)</li>
          </ul>
          <p>
            Unfortunately, the time of the JournalismAI fellowship is very
            limited and we were not able to evaluate this or similar approaches.
          </p>
          <h4>Evaluation</h4>
          <p>
            As a general training approach we decided to optimize for the
            precision of the detection of the mentions in comments. This
            strategy best fits the high speed moderation challenge the
            moderation team’s faces in everyday work. Our goal is to focus their
            attention only to comments that are very likely to contain a mention
            and not to confuse the moderation team with comments that don’t
            contain mentions.
          </p>
          <p>
            In addition we decided not to include the accuracy score in our
            evaluation because its high values are misleading for the
            interpretation of the evaluation. This effect is because of the
            strong imbalance in the distribution between comments with and
            without mentions. E.g., a classification that would label each
            comment as without mentions would receive an accuracy of 0.92
            percentage points of accuracy.
          </p>
          <figure>
            <img src={ImageTableB} alt="Test corpus metrics" loading="lazy" />
            <figcaption>
              Figure 1: Evaluation based on the test data (10% of the corpus)
            </figcaption>
          </figure>
          <p>
            Based on our train results in Figure 1, as well as preliminary human
            evaluation in Figure 2, we currently use the best model
            (GPT2-finetune) for our comment detection system. Please note, that
            the Named Entity Recognition approach was not part of the human
            evaluation because it was performing too poorly.
          </p>
          <figure>
            <img
              src={ImageTableA}
              alt="Human evaluation metrics"
              loading="lazy"
            />
            <figcaption>
              Figure 2: Positive comments evaluated by members of the Community
              Management in MS Teams
            </figcaption>
          </figure>
        </Waypoint>

        <Waypoint id="critical-discussion" action={setCurrentSection}>
          <h2 id="critical-discussion">
            Critical discussion:
            <br />
            Use of AI in a journalistic context
          </h2>

          <p>
            When using methods of artificial intelligence in a journalistic
            context, one should very consciously reflect on the advantages and
            disadvantages as well as the possibilities and limitations of these
            technologies. All participants in the project have been aware of
            this fact from the very beginning and have reflected on it again and
            again in the various phases of the project.
          </p>

          <p>
            Two main interests were in the foreground and thus also influenced
            the choice of technology. On the one hand, the aim was to create an
            application that was as useful as possible for the editorial offices
            and that could solve the problems posed sustainably and effectively.
            Secondly, the aim was to learn as much as possible about the use of
            NLP and machine learning algorithms for media houses and in
            journalism.{' '}
          </p>

          <h4>Rules-based programming versus NLP model</h4>

          <p>
            One of the most important questions at the beginning of a project
            is: Why do you want to use methods based on artificial intelligence?
            Is the problem posed and the possible solution better solvable with
            artificial intelligence or can classic rule-based programming be
            found that leads just as or perhaps even better to the goal?
          </p>

          <p>
            Both approaches have advantages and disadvantages. In the present
            project, it was decided to use NLP models to recognise topics in
            comments, because this method can analyse texts as a whole and not
            only recognise clear patterns such as names and titles. The
            disadvantage of this method is that it can only be used in a very
            specialised way and can therefore only be transferred to other
            projects and problems to a small extent. In addition, the quality of
            the solution, i.e. ultimately the actual detection accuracy, is
            highly dependent on the training data. The data required for
            successful training of the models has also been expanded with the
            help of artificial intelligence. Since only a very small part of all
            comments contain a positive addressing of the editorial offices in
            the technical sense, comments with newsroom addressing had to be
            supplemented with the help of the language model GPT-3.However,
            every single one of them was read by a member of our team and
            checked for suitability in terms of content and structure.
          </p>

          <h4>Bias and ethical standards</h4>

          <p>
            The strong dependence on the quality of the training data leads to
            another problem. How does one deal with ethical issues, for example
            that biases may be hidden in the data sets that strongly influence
            the results? For example,{' '}
            <a href="https://aclanthology.org/2021.nuse-1.5/">gender bias</a> is
            repeatedly reported for GPT-3. For this reason, we decided not to
            publish the training data for the GPT-3 model.
          </p>

          <p>
            When AI techniques are applied, it is particularly important to make
            editorial and ethical standards transparent. This allows mistakes to
            be identified in time and gives interested parties the opportunity
            to learn from developments.{' '}
          </p>

          <p>
            BR has set out its{' '}
            <a href="https://www.br.de/extra/ai-automation-lab/ki-ethik-100.html">
              rules for the use of AI and automation
            </a>{' '}
            for its AI and Automation Lab. MDR has published its approach in
            workshop reports following concrete projects, which can be read{' '}
            <a href="https://www.mdr.de/nachrichten/sachsen-anhalt/landtagswahl/was-wir-gerlernt-haben-code-veroeffentlicht-automatisierte-berichte-zur-landtagswahl-100.html">
              here
            </a>{' '}
            and{' '}
            <a href="https://www.mdr.de/nachrichten/sachsen-anhalt/landtagswahl/warum-wir-automatisierte-wahlberichte-fuer-gemeinden-und-wahlkreise-anbieten-100.html">
              here
            </a>
            .
          </p>

          <h4>Limits and opportunities of the project</h4>

          <p>
            In order to be able to train AI models in a useful way, clear
            definitions are needed – in our case of what is a really relevant
            addressing of the newsroom and what is not. When annotating the
            addresses with Prodigy, we noticed examples that were difficult to
            assign unambiguously. For example, statements can often only be
            understood as addressing the newsroom in the context of previous
            comments. And also the question of what is journalistically relevant
            and where one must react as an editorial team is in some cases a
            highly individual one and depends, among other things, on editorial
            rules, netiquettes and also journalistic judgement.
          </p>

          <p>
            As mentioned above, another limitation of the present project is the
            transferability to other problems. In addition, the actual use and
            the quality of the recognition in everyday editorial work will
            strongly depend on the continuous training of the data set. For this
            reason, an automated re-training is aspired to, but not developed at
            the moment.
          </p>

          <p>
            Nevertheless, it is very important to us to give other journalists
            and media houses as detailed an insight as possible into our work
            and to share our experiences, data and programme solutions. For this
            purpose, we have published most of the project under an open-source
            licence on{' '}
            <a href="https://github.com/br-data/wtwm-topic-modelling">GitHub</a>
            .
          </p>
        </Waypoint>

        <Waypoint id="learnings" action={setCurrentSection}>
          <h2 id="learnings">Learnings</h2>

          <figure>
            <img
              src={ImageLearnings}
              alt="Image on project phases"
              loading="lazy"
            />
            <figcaption>Source: LSE JournalismAI Fellowship</figcaption>
          </figure>

          <h4>Fail early and kill your darlings</h4>

          <p>
            At the beginning of the project, we developed various ideas and also
            received suggestions from the editorial offices. In particular, we
            pursued topic recognition in user comments for a long time and even
            formulated it as our initial thesis during interviews with our
            colleagues from community management. In the course of time,
            however, it became more and more apparent that there are already
            different providers for topic recognition that could be easily
            integrated into our systems and that, above all, there is not a need
            for this in all teams of BR and MDR Community Management. We then
            discarded the idea soon enough and concentrated on recognising
            mentions of the editorial team in comments.
          </p>

          <h4>It’s great to achieve something in 6 months, isn’t it!?</h4>

          <p>
            It’s wonderful to have developed a real AI system after an intensive
            work process of 6 months, which will possibly be integrated into the
            daily editorial routine. We have learned a lot in this process and
            are glad that this time limit has given a clear framework for our
            needed resources. However, it is also difficult to develop an AI
            system in such a short time that represents real innovation and goes
            beyond mere testing. In many decisions, time pressure is the
            determining factor and has the greatest impact on the development
            and scope of the project. However, as a longer duration of the
            fellowship would not be compatible with our work resources, we feel
            that the 6 months is the right balance of effort and outcome.
          </p>

          <h4>Data is great but how do I get it?</h4>
          <p>
            To get access to the actual comments were some of the steps that
            took us the longest time due to the size of our organizations, we
            had to ask for permission from multiple people. If we were to do
            such a project we would definitely ask for these permissions and
            insist on them as soon as possible.
          </p>

          <p>
            Especially in large companies, processes such as making data
            available take time. Legal and technical questions have to be
            clarified, work processes reviewed and the new requirements
            integrated into the existing infrastructure. Especially when it
            comes to handling user data, special protective measures are
            important. When planning and developing new solutions for editorial
            offices, it is essential to take these processes into account. A few
            concrete points that have helped us with this:
          </p>

          <ul>
            <li>
              Outline the main content and goals in a short project paper - you
              will be asked for this in every conversation.
            </li>
            <li>
              Take your time to find the right contact person for your questions
              – and don’t trust that your request will be forwarded to the right
              person.
            </li>
            <li>
              Be clear about what you are looking for: a prototype and lab test
              or the integration of the development into the production system.
            </li>
            <li>
              Specify how you will handle the data and who will have access to
              it.
            </li>
            <li>
              Clarify legal issues such as types of licences, use and
              publication of content in good time and set up your project in
              such a way that large parts of it can also be published as open
              source.
            </li>

            <li>With what were we going to gain the most learning?</li>
            <li>
              Was it possible to solve multiple of the problems with the same
              tool?
            </li>
          </ul>

          <h4>Labelling data is like running a marathon</h4>

          <p>
            At “🐶 What’s there, what’s missing” we try to strengthen a
            constructive dialogue with our readers by identifying mentions of
            the newsroom in user comments. The very idea of this indicates that
            there are not many such user comments to be found at present. But if
            you want to train an AI model that finds these comments, you need a
            lot of them. This means: You have to analyse all the more comments
            and mark them as positive (comment contains a mention of the
            newsroom) and negative (comment contains no mention). In our case,
            we analysed 17,451 comments and found 330 positive ones (comment
            with a newsroom mention).
          </p>

          <h4>Working and learning with other fellows is awesome</h4>

          <p>
            A great benefit of our fellowship is the exchange and joint learning
            with other journalists from different countries on the topic of AI.
            Here, the LSE has created a framework that sets exciting impulses:
            Each newsroom that applies has to set up a team of two members with
            journalistic and technical backgrounds. These teams then look for
            another team as a partner at the beginning of the fellowship and
            realise a project together with them. In our case, the BR and
            MDR/ida teams joined forces.
          </p>

          <p>
            As soon as this initial matching phase is over, the newly formed
            project teams start working. Unfortunately, from this point on, of
            course, getting to know the other fellows diminishes somewhat, as
            all participants focus on the implementation of their project. But
            especially in this phase, technical input, best practice examples,
            professional impulses and the exchange of content in a larger group
            are particularly important. Instead, the temptation is to keep the
            joint coordination as efficient as possible in order to be able to
            devote enough time to the project. The LSE team has already taken up
            this suggestion and would like to provide more space for input and
            exchange in this phase in the future. The open rounds with our
            global colleagues on AI topics have been particularly inspiring!
          </p>
        </Waypoint>

        <Waypoint id="about" action={setCurrentSection}>
          <h2 id="about">About the project</h2>

          <p>
            This project is part of the{' '}
            <a href="https://www.lse.ac.uk/media-and-communications/polis/JournalismAI/Fellowship-Programme">
              2022 JournalismAI Fellowship Programme
            </a>
            . The Fellowship brought together 46 journalists and technologists
            from across the world to collaboratively explore innovative
            solutions to improve journalism via the use of AI technologies. You
            can explore all the Fellowship projects{' '}
            <a href="https://www.lse.ac.uk/media-and-communications/polis/JournalismAI/Fellowship-Programme">
              at this link
            </a>
            .
          </p>

          <p>
            The project was developed as a collaboration between Bayerischer
            Rundfunk, Mitteldeutscher Rundfunk and the Innovations- und Digital
            Agentur. The fellows who contributed to the project are: Jaime
            Avalos Mongil (Junior Data Scientist, ida), Philipp Gawlik (Computer
            Linguist, BR), Martin Paul (Journalist & Product Developer, MDR),
            Jörg Pfeiffer (Product Manager, BR), supported by Natalie Widmann
            (ida), Katharina Forstmair (Data Journalist, MDR and ida) and Manuel
            Mohr (Data Journalist, MDR).
          </p>

          <p>
            <a href="https://www.lse.ac.uk/media-and-communications/polis/JournalismAI">
              JournalismAI
            </a>{' '}
            is a project of{' '}
            <a href="https://www.lse.ac.uk/media-and-communications/polis">
              Polis
            </a>{' '}
            – the journalism think-tank at the London School of Economics and
            Political Science – and it’s sponsored by the{' '}
            <a href="https://newsinitiative.withgoogle.com/">
              Google News Initiative
            </a>
            . If you want to know more about the Fellowship and the other
            JournalismAI activities,{' '}
            <a href="https://mailchi.mp/lse.ac.uk/journalismai">
              sign up for the newsletter
            </a>{' '}
            or get in touch with the team via{' '}
            <a href="mailto:hello@journalismai.info">hello@journalismai.info</a>
          </p>

          <h3>Links</h3>

          <ul>
            <li>
              Code and documentation on Github:
              <br />
              <a href="https://github.com/br-data/wtwm-topic-modelling">
                https://github.com/br-data/wtwm-topic-modelling
              </a>
            </li>

            <li>
              Model on Hugging Face:
              <br />
              <a href="https://huggingface.co/aiautomationlab/wtwm-gpt2-based-mentions-detector">
                https://huggingface.co/aiautomationlab/wtwm-gpt2-based-mentions-detector
              </a>
            </li>
          </ul>

          <h3>Disclaimer</h3>

          <p>
            The JournalismAI Fellowship at the London School of Economics and
            Political Science (LSE) claims to aim to empower media houses to use
            artificial intelligence responsibly. To achieve this, the Fellowship
            is part of LSE’s Journalism Think Tank (Polis) and is financially
            supported by the Google News Initiative. LSE has paid €1,047.20 for
            a service provider’s work (adapting their system to enable access
            for our team) from Fellowship funds available to all teams.
          </p>

          <h3>Imprint</h3>

          <ul>
            <li>
              <a href="https://www.br.de/unternehmen/service/impressum/index.html">
                Imprint Bayerischer Rundfunk
              </a>{' '}
              |{' '}
              <a href="https://www.br.de/nachrichten/datenschutz">
                Privacy policy Bayerischer Rundfunk
              </a>
            </li>
            <li>
              <a href="https://www.mdr.de/service/impressum/index.html">
                Imprint Mitteldeutscher Rundfunk
              </a>{' '}
              |{' '}
              <a href="https://www.mdr.de/service/datenschutz/index.html ">
                Privacy policy Mitteldeutscher Rundfunk
              </a>
            </li>
            <li>
              <a href="https://www.ida.me/impressum/">Imprint ida</a> |{' '}
              <a href="https://www.ida.me/datenschutzerklaerung/">
                Privacy policy ida
              </a>
            </li>
          </ul>

          <p className="bigMarginTop bigMarginBottom">
            <strong>Thanks to the JournalismAI Team and to all Fellows!</strong>
          </p>
        </Waypoint>
      </ContentArea>
    </ContentWrapper>
  );
};
